import { computed, ref } from 'vue'
import Network from '@/Network'
import type { CheckAttemptsFilters } from '@/Network/Types/Requests/CheckAttemptsRequests'
import type {
  CheckAttemptItem,
  CheckAttemptOutcome,
  CheckAttemptReason,
  CheckAttemptType,
  CheckAttemptsListResponse
} from '@/Network/Types/Responses/CheckAttemptsResponses'
import { outcomeColor, reasonLabel } from './checkAttemptsFormat'

export type CheckAttemptsSource = { healthUnitId: number } | { stationId: number }

export interface CheckAttemptsUiFilters {
  /** yyyy-MM-dd (input type=date) */
  startDate: string
  /** yyyy-MM-dd */
  endDate: string
  doctorId: number | null
  type: CheckAttemptType | null
  outcome: CheckAttemptOutcome | null
  reason: CheckAttemptReason | null
}

export const PAGE_SIZE = 50
export const DEFAULT_RANGE_DAYS = 7
export const MAX_RANGE_DAYS = 90

function toDateInput(d: Date): string {
  return d.toISOString().slice(0, 10)
}

export function defaultFilters(): CheckAttemptsUiFilters {
  const end = new Date()
  // "últimos 7 dias" inclui hoje: subtrai (DEFAULT_RANGE_DAYS - 1) dias para o range de
  // datas ter DEFAULT_RANGE_DAYS dias no total quando expandido para 00:00:00–23:59:59.999
  // em buildRequest (senão o intervalo em ISO fica ~1 dia maior que o esperado).
  const start = new Date(end.getTime() - (DEFAULT_RANGE_DAYS - 1) * 86_400_000)
  return {
    startDate: toDateInput(start),
    endDate: toDateInput(end),
    doctorId: null,
    type: null,
    outcome: null,
    reason: null
  }
}

export function isHealthUnitSource(s: CheckAttemptsSource): s is { healthUnitId: number } {
  return 'healthUnitId' in s
}

export function useCheckAttempts(
  source: CheckAttemptsSource,
  initial?: Partial<CheckAttemptsUiFilters>
) {
  const filters = ref<CheckAttemptsUiFilters>({ ...defaultFilters(), ...initial })
  const items = ref<CheckAttemptItem[]>([])
  const total = ref(0)
  const page = ref(1)
  const summary = ref<CheckAttemptsListResponse['summary'] | null>(null)
  const healthUnit = ref<CheckAttemptsListResponse['healthUnit']>(null)
  const doctors = ref<CheckAttemptsListResponse['doctors']>([])
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const selectedId = ref<number | null>(null)

  const pageCount = computed(() => Math.max(1, Math.ceil(total.value / PAGE_SIZE)))

  const points = computed(() =>
    items.value
      .filter((i) => i.checkLatitude != null && i.checkLongitude != null)
      .map((i) => ({
        id: i.id,
        lat: i.checkLatitude as number,
        lng: i.checkLongitude as number,
        color: outcomeColor(i.outcome),
        label: `${reasonLabel(i)} · ${i.doctor?.name ?? ''}`.trim()
      }))
  )

  function buildRequest(paginate = true): CheckAttemptsFilters {
    const f = filters.value
    const req: CheckAttemptsFilters = {
      type: f.type ?? undefined,
      outcome: f.outcome ?? undefined,
      reason: f.reason ?? undefined
    }
    if (paginate) {
      req.page = page.value
      req.limit = PAGE_SIZE
    }
    if (isHealthUnitSource(source)) {
      req.startDate = new Date(`${f.startDate}T00:00:00`).toISOString()
      req.endDate = new Date(`${f.endDate}T23:59:59.999`).toISOString()
      req.doctorId = f.doctorId ?? undefined
    }
    return req
  }

  async function load(): Promise<void> {
    isLoading.value = true
    error.value = null
    try {
      const req = buildRequest()
      const res = isHealthUnitSource(source)
        ? await Network.checkAttempts.listByHealthUnit(source.healthUnitId, req)
        : await Network.checkAttempts.listByStation(source.stationId, req)
      items.value = res.data
      total.value = res.total
      summary.value = res.summary
      healthUnit.value = res.healthUnit
      doctors.value = res.doctors
      if (selectedId.value != null && !res.data.some((i) => i.id === selectedId.value)) {
        selectedId.value = null
      }
    } catch (e) {
      items.value = []
      total.value = 0
      summary.value = null
      error.value =
        e instanceof Error ? e.message : 'Não foi possível carregar as tentativas de check'
    } finally {
      isLoading.value = false
    }
  }

  /** Exporta o .xlsx com os filtros atuais (sem paginação) e devolve o link para download. */
  async function exportXlsx(): Promise<HTMLAnchorElement> {
    const req = buildRequest(false)
    const f = filters.value
    const filename = isHealthUnitSource(source)
      ? `tentativas-de-check-${f.startDate}-${f.endDate}.xlsx`
      : `tentativas-de-check-plantao-${source.stationId}.xlsx`
    return isHealthUnitSource(source)
      ? Network.checkAttempts.exportXlsxByHealthUnit(source.healthUnitId, req, filename)
      : Network.checkAttempts.exportXlsxByStation(source.stationId, req, filename)
  }

  function resetFilters(): void {
    filters.value = defaultFilters()
    page.value = 1
  }

  function select(id: number | null): void {
    selectedId.value = selectedId.value === id ? null : id
  }

  return {
    filters,
    items,
    total,
    page,
    pageCount,
    summary,
    healthUnit,
    doctors,
    isLoading,
    error,
    selectedId,
    points,
    load,
    exportXlsx,
    resetFilters,
    select
  }
}
